import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, {useEffect, useRef, useState} from 'react'

import aiMark from './assets/ai-mark.svg'
import iconFramework from './assets/icon-framework.svg'
import iconPromise from './assets/icon-promise.svg'
import iconShift from './assets/icon-shift.svg'

const SECTION_TITLE = 'Meet Keyrus: Architect of intelligence'
const SECTION_TITLE_LINE_1 = 'Meet Keyrus:'
const SECTION_TITLE_LINE_2 = 'Architect of intelligence'
const SECTION_SUBTITLE = 'We operationalize intelligence, not AI tools'

const REVEAL_EASE = 'cubic-bezier(0.25, 1, 0.5, 1)'
const REVEAL_TRANSITION = `transform 0.9s ${REVEAL_EASE}, opacity 0.9s ease`

const CARDS = [
  {
    id: 'shift',
    side: 'left',
    icon: iconShift,
    iconAlt: '',
    title: 'The Shift',
    bodyLead:
      'Distributed intelligence becomes the infrastructure of the enterprise. A new',
    bodyEmphasis:
      'category emerges: businesses that run on intelligence, not software.',
    delay: 0.3,
  },
  {
    id: 'promise',
    side: 'right',
    icon: iconPromise,
    iconAlt: '',
    title: 'The Promise',
    bodyLead: 'We move organizations from experimental AI to industrial AI.',
    bodyEmphasis:
      'Isolated agents to orchestrated systems, from insight to execution.',
    delay: 0.6,
  },
  {
    id: 'framework',
    side: 'right',
    icon: iconFramework,
    iconAlt: '',
    title: 'The Framework',
    bodyLead:
      'The Human Orchestrated Model™ — three layers that turn data, agents',
    bodyEmphasis: 'and human governance into measurable performance.',
    delay: 0.9,
  },
]

const useReveal = () => {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const node = ref.current

    if (!node) {
      return undefined
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      setIsVisible(true)

      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.unobserve(entry.target)
          }
        })
      },
      {threshold: 0.15},
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return [ref, isVisible]
}

const getRevealStyle = (isVisible, fromTransform, delay) => ({
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translate(0, 0)' : fromTransform,
  transition: REVEAL_TRANSITION,
  transitionDelay: isVisible ? `${delay}s` : '0s',
  willChange: 'transform, opacity',
})

const getStyles = (theme, isMobile) => {
  const palette = theme.palette || {}
  const background = palette.background || {}
  const text = palette.text || {}
  const surface = background.default || '#ffffff'
  const titleColor = text.dark || '#000000'
  const bodyColor = text.primary || '#333333'
  const subtitleColor = text.primary || '#333333'
  const connector = background.gray || '#d8d8d8'

  return {
    root: {
      position: 'relative',
      width: '100%',
      background: surface,
      color: bodyColor,
      padding: isMobile ? '64px 16px 80px' : '120px 24px 160px',
      scrollMarginTop: isMobile ? 20 : 90,
    },
    inner: {
      maxWidth: 1280,
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: isMobile ? 48 : 88,
    },
    title: {
      margin: 0,
      fontSize: isMobile ? 22 : 'clamp(28px, 4vw, 56px)',
      lineHeight: isMobile ? 1.22 : 1.18,
      fontWeight: 800,
      color: titleColor,
    },
    subtitle: {
      margin: '16px 0 0',
      fontSize: 'clamp(16px, 1.8vw, 26px)',
      lineHeight: isMobile ? 1.55 : 1.35,
      fontWeight: isMobile ? 300 : 400,
      color: subtitleColor,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : 'minmax(260px, 1fr) auto minmax(260px, 1fr)',
      gap: isMobile ? 0 : 32,
      alignItems: 'center',
    },
    aiSlot: {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      order: isMobile ? -1 : 0,
      marginBottom: isMobile ? 32 : 0,
    },
    aiMark: {
      width: isMobile ? 'min(280px, 70vw)' : 'min(420px, 30vw)',
      height: 'auto',
      display: 'block',
    },
    rightColumn: {
      display: 'grid',
      gap: isMobile ? 0 : 96,
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      textAlign: 'left',
      gap: isMobile ? 14 : 12,
      paddingBottom: isMobile ? 26 : 0,
    },
    iconRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      width: '100%',
      borderBottom: isMobile ? '1px solid rgba(0,0,0,0.12)' : 'none',
      paddingBottom: isMobile ? 12 : 0,
    },
    iconCircle: {
      width: 47,
      height: 47,
      borderRadius: '50%',
      background: '#171531',
      border: '0.6px solid rgba(255,255,255,0.35)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    iconInner: {
      width: 24,
      height: 24,
    },
    icon: {
      width: 56,
      height: 56,
      flexShrink: 0,
    },
    connector: {
      height: 2,
      background: connector,
      display: isMobile ? 'none' : 'block',
    },
    connectorLeft: {
      width: 'calc(100% + 120px)',
    },
    connectorRight: {
      width: 'calc(100% + 44px)',
      marginLeft: -44,
    },
    cardTitle: {
      margin: 0,
      fontSize: isMobile ? 20 : 'clamp(20px, 1.6vw, 26px)',
      fontWeight: 700,
      color: titleColor,
      lineHeight: 1.2,
    },
    cardBody: {
      margin: 0,
      maxWidth: 460,
      fontSize: isMobile ? 16 : 'clamp(14px, 1vw, 16px)',
      lineHeight: isMobile ? 1.3 : 1.55,
      color: bodyColor,
    },
    cardEmphasis: {
      fontWeight: 700,
      color: titleColor,
    },
  }
}

const KeyrusAiAbout = ({copy, isMobile}) => {
  const theme = useTheme()
  const styles = getStyles(theme, isMobile)
  const [sectionRef, isVisible] = useReveal()

  const cardCopy = copy.cards || {}
  const localizeCard = card => ({
    ...card,
    title: cardCopy[card.id]?.title || card.title,
    bodyLead: cardCopy[card.id]?.bodyLead || card.bodyLead,
    bodyEmphasis: cardCopy[card.id]?.bodyEmphasis || card.bodyEmphasis,
  })

  const titleLine1 = copy.titleLine1 || SECTION_TITLE_LINE_1
  const titleLine2 = copy.titleLine2 || SECTION_TITLE_LINE_2
  const titleInline = copy.titleLine1
    ? `${titleLine1} ${titleLine2}`
    : SECTION_TITLE
  const subtitle = copy.subtitle || SECTION_SUBTITLE

  const renderCard = card => {
    const isLeft = card.side === 'left'
    const fromTransform = isMobile
      ? 'translateY(30px)'
      : `translateX(${isLeft ? '-40px' : '40px'})`

    const cardStyle = {
      ...styles.card,
      ...getRevealStyle(isVisible, fromTransform, card.delay),
    }

    const body = (
      <p style={styles.cardBody}>
        {card.bodyLead}{' '}
        <span style={styles.cardEmphasis}>{card.bodyEmphasis}</span>
      </p>
    )

    if (isMobile) {
      return (
        <article key={card.id} style={cardStyle}>
          <div style={styles.iconRow}>
            <div style={styles.iconCircle}>
              <img
                alt={card.iconAlt}
                src={card.icon}
                style={styles.iconInner}
              />
            </div>
            <h3 style={styles.cardTitle}>{card.title}</h3>
          </div>
          {body}
        </article>
      )
    }

    return (
      <article key={card.id} style={cardStyle}>
        <img alt={card.iconAlt} src={card.icon} style={styles.icon} />
        <span
          aria-hidden="true"
          style={{
            ...styles.connector,
            ...(isLeft ? styles.connectorLeft : styles.connectorRight),
          }}
        />
        <h3 style={styles.cardTitle}>{card.title}</h3>
        {body}
      </article>
    )
  }

  const leftCard = localizeCard(CARDS.find(c => c.side === 'left'))
  const rightCards = CARDS.filter(c => c.side === 'right').map(localizeCard)

  return (
    <section id="meet-keyrus" ref={sectionRef} style={styles.root}>
      <div style={styles.inner}>
        <header
          style={{
            ...styles.header,
            ...getRevealStyle(isVisible, 'translateY(24px)', 0),
          }}
        >
          <h2 style={styles.title}>
            {isMobile ? (
              <>
                {titleLine1}
                <br />
                {titleLine2}
              </>
            ) : (
              titleInline
            )}
          </h2>
          <p style={styles.subtitle}>{subtitle}</p>
        </header>
        <div style={styles.grid}>
          {renderCard(leftCard)}
          <div
            style={{
              ...styles.aiSlot,
              ...getRevealStyle(isVisible, 'translateY(30px)', 0),
            }}
          >
            <img alt="" src={aiMark} style={styles.aiMark} />
          </div>
          <div style={styles.rightColumn}>{rightCards.map(renderCard)}</div>
        </div>
      </div>
    </section>
  )
}

KeyrusAiAbout.propTypes = {
  copy: PropTypes.shape({
    cards: PropTypes.objectOf(
      PropTypes.shape({
        bodyEmphasis: PropTypes.string,
        bodyLead: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    subtitle: PropTypes.string,
    titleLine1: PropTypes.string,
    titleLine2: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
}

KeyrusAiAbout.defaultProps = {
  copy: {},
  isMobile: false,
}

export default KeyrusAiAbout
